import type {PointerEvent, ReactElement} from 'react';
import {useRef} from 'react';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import {getSelectedGridItems, toggleGridItemSelection} from '@Components/base-grid';
import {LazyMasonryGrid} from '@Components/lazy-masonry-grid';
import {buildSkeletonGridItem, makeGeneratedMediaGetItems} from '@Components/lazy-masonry-grid/generated-media-grid-library';
import type {AiVideoBackendData} from '@Libraries/ai-video.library';
import {getGeneratedAiVideos} from '@Libraries/ai-video.library';
import {GeneratedVideoItem} from '@Panels/ai-video-panel/components/generated-video-item/generated-video-item';
import type {GeneratedVideoGridProps} from './generated-video-grid.types';
import {GENERATED_VIDEO_GRID_ID, GENERATED_VIDEO_GRID_ITEM_HEIGHT} from './generated-video-grid.types';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {Text, TextSize} from '@Components/text';
import {PulsatingThreeDots} from '@Components/pulsating-three-dots';
import {selectGeneratingAiVideos} from '@Components/poster-editor/poster-editor-reducer';
import {getAiVideoSkeletonDimensions} from '@Panels/ai-video-panel/ai-video-panel.library';
import type {AiVideoAspectRatio} from '@Panels/ai-video-panel/ai-video-panel.types';
import {isMobile} from 'react-device-detect';
import {beginTileDrag} from '@Libraries/canvas-item-drag.library';

export function GeneratedVideoGrid(props: GeneratedVideoGridProps): ReactElement {
  const dispatch = useAppDispatch();
  const onFullscreenPreviewRef = useRef(props.onFullscreenPreview);
  onFullscreenPreviewRef.current = props.onFullscreenPreview;
  const generatingItems = useAppSelector(selectGeneratingAiVideos);
  const generatingItemsRef = useRef(generatingItems);
  generatingItemsRef.current = generatingItems;
  const isGenerating = generatingItems.length > 0;
  const isInMultiSelectionMode = props.isFullScreen || isMobile || (props.enableMultiSelection ?? false);
  const selectedItems = useAppSelector((state) => getSelectedGridItems(state, GENERATED_VIDEO_GRID_ID));

  const getGridItem = (video: AiVideoBackendData): GridItemStorage => {
    return {
      type: GRID_ITEM_TYPE.CUSTOM,
      id: `${video.id}`,
      width: video.videoProps.width,
      height: video.videoProps.height,
      data: {video},
      children: (
        <GeneratedVideoItem
          key={video.id}
          item={video}
          onFullscreenPreview={(): void => {
            onFullscreenPreviewRef.current(video);
          }}
        />
      ),
    };
  };

  const getVideoForGridItem = (item: GridItemStorage): AiVideoBackendData | undefined => {
    return (item.data as {video?: AiVideoBackendData} | undefined)?.video;
  };

  const onItemPointerDown = (item: GridItemStorage, event: PointerEvent<HTMLElement>): void => {
    const video = getVideoForGridItem(item);
    if (!video) {
      return;
    }
    beginTileDrag({
      event,
      onDropOnCanvas: (point) => {
        props.onAddToDesign(video, point);
      },
      cloneFullElement: true,
    });
  };

  const onGridItemClicked = (item: GridItemStorage, isDoubleClick: boolean): void => {
    const video = getVideoForGridItem(item);
    if (!video) {
      return;
    }
    if (isDoubleClick && selectedItems.length === 1 && selectedItems[0].id === item.id) {
      props.onAddToDesign(video);
      return;
    }
    if (isInMultiSelectionMode) {
      dispatch(toggleGridItemSelection({gridItemId: item.id, gridSelectionGroupId: GENERATED_VIDEO_GRID_ID}));
      return;
    }
    props.onAddToDesign(video);
  };

  const getItems = makeGeneratedMediaGetItems({
    gridId: GENERATED_VIDEO_GRID_ID,
    fetchItems: getGeneratedAiVideos,
    buildGridItem: getGridItem,
    getGeneratingSkeletons: () =>
      generatingItemsRef.current.map((item) => {
        const {width, height} = getAiVideoSkeletonDimensions(item.params.aspectRatio as AiVideoAspectRatio);
        return buildSkeletonGridItem({id: item.id, width, height});
      }),
    onItemsLoaded: (videos): void => {
      props.onVideosLoaded(videos);
    },
  });

  const getGeneratingText = (): ReactElement | null => {
    if (isGenerating) {
      return (
        <>
          <Text className={'content-ai inline-flexbox spacing-p-b-2'} size={TextSize.MD} val={window.i18next.t('pmwjs_this_may_take_a_few_minutes')} />
          <PulsatingThreeDots className={'content-secondary spacing-m-l-1'} />
        </>
      );
    }
    return null;
  };

  return (
    <div className={'spacing-p-b-4'}>
      {getGeneratingText()}
      <LazyMasonryGrid
        key={GENERATED_VIDEO_GRID_ID}
        gridId={GENERATED_VIDEO_GRID_ID}
        gridSelectionGroupId={GENERATED_VIDEO_GRID_ID}
        getItems={getItems}
        defaultHeight={GENERATED_VIDEO_GRID_ITEM_HEIGHT}
        customOnGridItemClicked={onGridItemClicked}
        maxSelectionNumber={props.maxSelectionNumber ?? undefined}
        loadItemBatchSize={20}
        clearGridOnUnmount
        makeContentScrollable={false}
        showHoverOverlay={false}
        showSelectionOverlay={isInMultiSelectionMode}
        isItemDraggable={props.enableDragToCanvas}
        onItemPointerDown={onItemPointerDown}
        className="_full-width"
      />
    </div>
  );
}
